// pages/reports/SalesReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
    ChevronUp, 
    RotateCcw, 
    Download,
    Printer,
    DollarSign,
    TrendingUp,
    TrendingDown,
    PieChart,
    FileText,
    Calendar,
    Users,
    ShoppingBag,
    CreditCard,
    RefreshCw,
    BarChart2,
   
} from 'feather-icons-react/build/IconComponents';
import { reportActions } from '../../core/action/reportActions';

const MySwal = withReactContent(Swal);

const SalesReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    
    // State
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [compareWith, setCompareWith] = useState('');
    const [activeTab, setActiveTab] = useState('summary');
    
    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate),
                compare_with: compareWith
            };
            
            const result = await reportActions.getSalesReport(params);
            debugger;
            if (result.success) {
                setReportData(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, compareWith]);
    
    // Load on mount and when dependencies change
    useEffect(() => {
        loadReport();
    }, [loadReport]);
    
    // Handle export PDF
    const handleExportPDF = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToPDF('sales', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `sales_report_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export PDF',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle export Excel
    const handleExportExcel = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToExcel('sales', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `sales_report_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting Excel:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export Excel',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle print
    const handlePrint = () => {
        window.print();
    };
    
    // Compare options
    const compareOptions = [
        { value: '', label: 'No Comparison' },
        { value: 'previous', label: 'Previous Period' }
    ];
    
    // Render summary cards
    const renderSummaryCards = () => {
        if (!reportData) return null;
        
        const { summary } = reportData;
        const hasGrowth = reportData.comparison?.growth_percentage > 0;
        
        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Sales</p>
                                    <h3 className="mb-0 text-primary">
                                        {reportActions.formatCurrency(summary.total_sales)}
                                    </h3>
                                    <small className="text-muted">{summary.total_invoices} invoices</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-primary" />
                                </div>
                            </div>
                            {reportData.comparison && (
                                <div className="mt-2">
                                    <span className={`badge ${hasGrowth ? 'bg-success' : 'bg-danger'}`}>
                                        {hasGrowth ? '+' : ''}{reportData.comparison.growth_percentage}% vs previous
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Paid</p>
                                    <h3 className="mb-0 text-success">
                                        {reportActions.formatCurrency(summary.total_paid)}
                                    </h3>
                                    <small className="text-muted">Collected amount</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <DollarSign size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Due</p>
                                    <h3 className="mb-0 text-danger">
                                        {reportActions.formatCurrency(summary.total_due)}
                                    </h3>
                                    <small className="text-muted">Pending payments</small>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <TrendingDown size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Avg. Sale Value</p>
                                    <h3 className="mb-0 text-info">
                                        {reportActions.formatCurrency(summary.average_sale_value)}
                                    </h3>
                                    <small className="text-muted">Per invoice</small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <BarChart2 size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render filter bar
    const renderFilterBar = () => {
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={setFromDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={setToDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Compare with</label>
                            <Select
                                options={compareOptions}
                                value={compareOptions.find(opt => opt.value === compareWith)}
                                onChange={(opt) => setCompareWith(opt?.value || '')}
                                placeholder="Select comparison"
                                isClearable
                            />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={loadReport}>
                                <RefreshCw size={16} className="me-2" />
                                Generate Report
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render tabs
    const renderTabs = () => {
        const tabs = [
            { id: 'summary', label: 'Summary', icon: <PieChart size={16} /> },
            { id: 'daily', label: 'Daily Breakdown', icon: <Calendar size={16} /> },
            { id: 'customers', label: 'Customers', icon: <Users size={16} /> },
            { id: 'products', label: 'Products', icon: <ShoppingBag size={16} /> },
            { id: 'payments', label: 'Payments', icon: <CreditCard size={16} /> }
        ];
        
        return (
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span className="ms-2">{tab.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    };
    
    // Render summary tab
    const renderSummaryTab = () => {
        if (!reportData) return null;
        
        const { summary, status_breakdown } = reportData;
        
        return (
            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Sales Overview</h6>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <td>Total Invoices</td>
                                        <td className="text-end"><strong>{summary.total_invoices}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Total Customers</td>
                                        <td className="text-end"><strong>{summary.total_customers}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Subtotal</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(summary.total_subtotal)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Tax</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(summary.total_tax)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Discount</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(summary.total_discount)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Shipping</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(summary.total_shipping)}</strong></td>
                                    </tr>
                                    <tr className="table-primary">
                                        <td><strong>Total Sales</strong></td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(summary.total_sales)}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Status Breakdown</h6>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th className="text-end">Count</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {status_breakdown.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <span className={`badge bg-${item.status === 'delivered' ? 'success' : 'warning'}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className="text-end">{item.count}</td>
                                            <td className="text-end">{reportActions.formatCurrency(item.total_amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render daily breakdown tab
    const renderDailyTab = () => {
        if (!reportData?.daily_breakdown) return null;
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Daily Sales Breakdown</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th className="text-end">Invoices</th>
                                    <th className="text-end">Total Sales</th>
                                    <th className="text-end">Total Paid</th>
                                    <th className="text-end">Total Due</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.daily_breakdown.map((day, idx) => (
                                    <tr key={idx}>
                                        <td>{reportActions.formatDate(day.date)}</td>
                                        <td className="text-end">{day.invoice_count}</td>
                                        <td className="text-end">{reportActions.formatCurrency(day.total_sales)}</td>
                                        <td className="text-end text-success">{reportActions.formatCurrency(day.total_paid)}</td>
                                        <td className="text-end text-danger">{reportActions.formatCurrency(day.total_due)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render customers tab
    const renderCustomersTab = () => {
        if (!reportData?.customer_breakdown) return null;
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Customer Performance</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Customer</th>
                                    <th>Phone</th>
                                    <th className="text-end">Invoices</th>
                                    <th className="text-end">Total Sales</th>
                                    <th className="text-end">Total Paid</th>
                                    <th className="text-end">Total Due</th>
                                    <th>Last Purchase</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.customer_breakdown.map((customer, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{customer.customer_name}</strong>
                                        </td>
                                        <td>{customer.phone || '-'}</td>
                                        <td className="text-end">{customer.invoice_count}</td>
                                        <td className="text-end">{reportActions.formatCurrency(customer.total_sales)}</td>
                                        <td className="text-end text-success">{reportActions.formatCurrency(customer.total_paid)}</td>
                                        <td className="text-end text-danger">{reportActions.formatCurrency(customer.total_due)}</td>
                                        <td>{reportActions.formatDate(customer.last_purchase_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render products tab
    const renderProductsTab = () => {
        if (!reportData?.top_products) return null;
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Top Selling Products</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th className="text-end">Quantity Sold</th>
                                    <th className="text-end">Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.top_products.map((product, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{product.product_name}</strong>
                                        </td>
                                        <td>{product.sku}</td>
                                        <td className="text-end">{product.total_quantity}</td>
                                        <td className="text-end">{reportActions.formatCurrency(product.total_revenue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render payments tab
    const renderPaymentsTab = () => {
        if (!reportData?.payment_breakdown) return null;
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Payment Method Breakdown</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Payment Method</th>
                                    <th className="text-end">Transactions</th>
                                    <th className="text-end">Total Amount</th>
                                    <th className="text-end">Percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.payment_breakdown.map((payment, idx) => {
                                    const percentage = (payment.total_amount / reportData.summary.total_paid) * 100;
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <span className="badge bg-secondary">
                                                    {payment.payment_method?.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td className="text-end">{payment.payment_count}</td>
                                            <td className="text-end">{reportActions.formatCurrency(payment.total_amount)}</td>
                                            <td className="text-end">{percentage.toFixed(2)}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);
    
    if (loading && !reportData) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Sales Report...</h5>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Sales Report</h4>
                            <h6>Analyze your sales performance</h6>
                        </div>
                    </div>
                    
                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-danger" onClick={handleExportPDF}>
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success" onClick={handleExportExcel}>
                                    <FileText size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={loadReport}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>
                
                {/* Period Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Period:</strong> {reportActions.formatDate(reportData?.period.from_date)} to {reportActions.formatDate(reportData?.period.to_date)}
                                    </div>
                                    {reportData?.comparison && (
                                        <div>
                                            <strong>Comparison:</strong> Previous period ({reportActions.formatDate(reportData.comparison.period.from)} to {reportActions.formatDate(reportData.comparison.period.to)})
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Filter Bar */}
                {renderFilterBar()}
                
                {/* Summary Cards */}
                {renderSummaryCards()}
                
                {/* Tabs */}
                {renderTabs()}
                
                {/* Tab Content */}
                {activeTab === 'summary' && renderSummaryTab()}
                {activeTab === 'daily' && renderDailyTab()}
                {activeTab === 'customers' && renderCustomersTab()}
                {activeTab === 'products' && renderProductsTab()}
                {activeTab === 'payments' && renderPaymentsTab()}
            </div>
        </div>
    );
};

export default SalesReport;